import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import s from './NovaCommissionModal.module.sass';
import c from './NovaCodeModal.module.sass';
import { closeUnlessAdminConsumed } from './modalKit';
import {
    UserPlus, X, UserRound, Landmark, Tag, CreditCard, Briefcase,
    Users, Check, MailOpen, ChevronDown, AlertCircle,
    ClipboardList, KeyRound, Rocket, ArrowLeftRight, ShieldCheck,
} from 'lucide-react';

// ── Кастомный дропдаун Nova: кнопка-значение + всплывающий список.
// Никаких нативных select — стиль и поведение полностью свои
function NovaSelect({ value, placeholder, options, onChange, leadingIcon: LeadingIcon }) {
    const [open, setOpen] = useState(false);
    const ref = useRef(null);

    useEffect(() => {
        if (!open) return undefined;
        const onDown = (e) => {
            if (ref.current && !ref.current.contains(e.target)) setOpen(false);
        };
        const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
        document.addEventListener('mousedown', onDown);
        document.addEventListener('keydown', onKey);
        return () => {
            document.removeEventListener('mousedown', onDown);
            document.removeEventListener('keydown', onKey);
        };
    }, [open]);

    const selected = options.find((o) => o.value === value) || null;

    return (
        <div className={c.select} ref={ref}>
            <button
                type="button"
                className={`${c.control} ${c.selectBtn} ${open ? c.controlFocus : ''}`}
                onClick={() => setOpen((v) => !v)}
                aria-haspopup="listbox"
                aria-expanded={open}
            >
                {selected?.icon
                    ? <img className={c.optIcon} src={selected.icon} alt="" />
                    : (LeadingIcon && <LeadingIcon size={13} strokeWidth={2} className={c.selectLeadIcon} aria-hidden="true" />)}
                <span className={`${c.selectValue} ${!selected ? c.selectPlaceholder : ''}`}>
                    {selected?.label || placeholder}
                </span>
                <ChevronDown
                    size={14}
                    strokeWidth={2.1}
                    className={`${c.selectChevron} ${open ? c.selectChevronUp : ''}`}
                    aria-hidden="true"
                />
            </button>
            {open && (
                <div className={c.selectMenu} role="listbox">
                    {options.map((o) => (
                        <button
                            key={o.value}
                            type="button"
                            role="option"
                            aria-selected={o.value === value}
                            className={`${c.option} ${o.value === value ? c.optionActive : ''}`}
                            onClick={() => { onChange(o.value); setOpen(false); }}
                        >
                            {o.icon && <img className={c.optIcon} src={o.icon} alt="" />}
                            <span className={c.optionLabel}>{o.label}</span>
                            {o.value === value && <Check size={13} strokeWidth={2.4} className={c.optCheck} aria-hidden="true" />}
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
}

// ── Таймлайн активации: три вехи, между ними — линии прогресса.
// Показывает без слов, где мы сейчас и что будет дальше
function Timeline({ items }) {
    return (
        <div className={c.steps}>
            {items.map((it, i) => (
                <React.Fragment key={it.label}>
                    {i > 0 && (
                        <span
                            className={`${c.stepLink} ${items[i - 1].state === 'done' ? c.stepLinkDone : ''}`}
                            aria-hidden="true"
                        />
                    )}
                    <div className={`${c.step} ${c[`step_${it.state}`]}`}>
                        <span className={c.stepDot}>
                            {it.state === 'done'
                                ? <Check size={13} strokeWidth={2.8} aria-hidden="true" />
                                : <it.icon size={13} strokeWidth={2.1} aria-hidden="true" />}
                            {it.state === 'active' && <span className={c.stepPing} aria-hidden="true" />}
                        </span>
                        <span className={c.stepLabel}>{it.label}</span>
                    </div>
                </React.Fragment>
            ))}
        </div>
    );
}

// ── Лицензия брокера: проверенный статус — зелёный чип со щитом
function LicenseChip({ license, safeT }) {
    return (
        <span className={c.licChip}>
            <ShieldCheck size={11} strokeWidth={2.3} aria-hidden="true" />
            {safeT('modals.code.brokerDetails.license', 'Licencia')}
            <b>{license || 'XXXXXX'}</b>
        </span>
    );
}

// ── Оставшиеся места: цветовой код дефицита + мини-шкала заполненности.
// Даблклик по значению — быстрое редактирование прямо на месте
function SpotsMeter({ spots, onChange, align = 'end', showIcon = true }) {
    const [editing, setEditing] = useState(false);
    const [draft, setDraft] = useState('');

    const m = String(spots || '').match(/(\d+)\s*\/\s*(\d+)/);
    const left = m ? parseInt(m[1], 10) : null;
    const total = m ? parseInt(m[2], 10) : 30;
    // Дефицит мест: <5 — красный, 5–15 — жёлтый, дальше — зелёный
    const tone = left == null ? '' : left < 5 ? c.spotsLow : left <= 15 ? c.spotsMid : c.spotsHigh;
    const pct = left != null && total > 0 ? Math.max(0, Math.min(100, (left / total) * 100)) : 0;

    const commit = () => {
        setEditing(false);
        const v = draft.trim().replace(/\s+/g, '');
        if (/^\d+\/\d+$/.test(v)) onChange?.(v);
    };

    return (
        <div
            className={`${c.spots} ${tone} ${align === 'start' ? c.spotsStart : ''}`}
            onDoubleClick={() => { setDraft(spots || ''); setEditing(true); }}
        >
            {editing ? (
                <input
                    className={c.spotsInput}
                    autoFocus
                    value={draft}
                    onChange={(e) => setDraft(e.target.value)}
                    onBlur={commit}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter') commit();
                        if (e.key === 'Escape') setEditing(false);
                    }}
                />
            ) : (
                <span className={c.spotsValue}>
                    {showIcon && <Users size={12} strokeWidth={2.2} aria-hidden="true" />}
                    {spots || '—'}
                </span>
            )}
            <span className={c.spotsBar} aria-hidden="true">
                <span style={{ width: `${pct}%` }} />
            </span>
        </div>
    );
}

// Аватарка брокера из настроек: имя файла резолвим через /data/avatar/,
// абсолютные и http-пути не трогаем
const resolveAvatarSrc = (avatar) => {
    if (!avatar) return null;
    return (avatar.startsWith('/') || avatar.startsWith('http'))
        ? avatar
        : `/data/avatar/${encodeURIComponent(avatar)}`;
};

const initialsOf = (name) => {
    if (!name) return '·';
    const parts = String(name).trim().split(/\s+/);
    return parts.slice(0, 2).map((p) => p[0]?.toUpperCase() || '').join('') || '·';
};

/**
 * NovaCodeModal — регистрация клиента брокером.
 *
 * Одна история в две стадии:
 *  • mode="input"  — брокер заводит данные клиента (форма + код);
 *  • mode="review" — данные заведены, ждём код активации с почты клиента.
 *    Скрин этого экрана уходит клиенту, поэтому код — герой композиции,
 *    а таймлайн сверху объясняет: остался один шаг до активного счёта.
 */
export default function NovaCodeModal({
    mode = 'review',
    isClosing = false,
    safeT = (k, d) => d,
    onClose,
    onConfirm,
    // client — review (только чтение)
    clientName,
    clientBank, // { name, img }
    clientTariffDisplay,
    clientAccount,
    // client — input (редактируемые)
    nameValue, onNameChange,
    bankOptions = [], bankValue, onBankChange,
    tariffOptions = [], tariffValue, onTariffChange,
    accountValue, onAccountChange,
    // подписи
    accountLabel,
    // broker
    brokerName, brokerLicense, brokerSpots, onSpotsChange, brokerAvatar,
    // код
    codeValue = '', onCodeChange, codeError, codeHint,
}) {
    const host = typeof document !== 'undefined'
        ? (document.querySelector('[data-nova-theme]') || document.body)
        : null;

    const isInput = mode === 'input';
    const codeRef = useRef(null);
    const nameRef = useRef(null);

    useEffect(() => {
        const id = setTimeout(() => {
            (isInput ? nameRef : codeRef).current?.focus();
        }, 140);
        return () => clearTimeout(id);
    }, [isInput]);

    useEffect(() => {
        const onKey = (e) => { if (e.key === 'Escape') onClose?.(); };
        document.addEventListener('keydown', onKey);
        return () => document.removeEventListener('keydown', onKey);
    }, [onClose]);

    if (!host) return null;

    const cells = Array.from({ length: 6 }, (_, i) => codeValue[i] || '');
    const activeIdx = Math.min(codeValue.length, 5);
    const brokerAvatarSrc = resolveAvatarSrc(brokerAvatar);

    // В режиме кода данные уже заведены — заголовок говорит о подтверждении
    // и называет клиента по имени. Экстремально длинные имена режем сами:
    // CSS-эллипсис в шапке — страховка, но лучше не раздувать строку
    const displayName = clientName && clientName.length > 28
        ? `${clientName.slice(0, 28).trimEnd()}…`
        : clientName;
    const title = !isInput && displayName
        ? safeT('modals.code.confirmTitle', 'Confirmación de registro: {name}').replace('{name}', displayName)
        : safeT('modals.code.title', 'Registro de cliente');
    const subtitle = isInput
        ? safeT('modals.code.subtitleInput', 'Complete los datos del cliente')
        : safeT('modals.code.subtitle', 'Ingrese el código de verificación');

    const timeline = [
        {
            icon: ClipboardList,
            label: safeT('modals.code.steps.data', 'Datos del cliente'),
            state: isInput ? 'active' : 'done',
        },
        {
            icon: KeyRound,
            label: safeT('modals.code.steps.code', 'Código por correo'),
            state: isInput ? 'todo' : 'active',
        },
        {
            icon: Rocket,
            label: safeT('modals.code.steps.active', 'Cuenta activa'),
            state: 'todo',
        },
    ];

    // ── Блок ввода кода (общий; в review — крупнее, «герой» экрана)
    const codeSection = (
        <section className={`${c.codeSection} ${!isInput ? c.codeHero : ''}`}>
            <div className={c.codeHead}>
                <span className={c.codeGlyph}>
                    <MailOpen size={16} strokeWidth={2} aria-hidden="true" />
                </span>
                <div className={c.codeHeadText}>
                    <span className={c.codeTitle}>
                        {safeT('modals.code.codeTitle', 'Código de activación')}
                    </span>
                    <span className={c.codeSub}>{codeHint}</span>
                </div>
            </div>
            <div
                className={`${c.codeField} ${codeError ? c.codeFieldError : ''}`}
                onClick={() => codeRef.current?.focus()}
            >
                <input
                    ref={codeRef}
                    className={c.codeHiddenInput}
                    value={codeValue}
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    maxLength={6}
                    onChange={(e) => onCodeChange?.(e.target.value.replace(/\D/g, '').slice(0, 6))}
                />
                <div className={c.codeCells} aria-hidden="true">
                    {cells.map((ch, i) => (
                        <div
                            key={i}
                            className={`${c.cell} ${ch ? c.cellFilled : ''} ${i === activeIdx && codeValue.length < 6 ? c.cellActive : ''}`}
                        >
                            {ch || <span className={c.cellDash} />}
                        </div>
                    ))}
                </div>
            </div>
            {codeError ? (
                <p className={c.codeError}>
                    <AlertCircle size={13} strokeWidth={2.2} aria-hidden="true" />
                    {safeT('modals.code.codeInput.error', 'El código es incorrecto. Verifique e intente de nuevo.')}
                </p>
            ) : (
                <p className={c.codeNote}>
                    <Rocket size={12} strokeWidth={2} aria-hidden="true" />
                    {safeT('modals.code.activationNote', 'Al confirmar el código, la cuenta quedará activa y el cliente podrá comenzar a operar.')}
                </p>
            )}
        </section>
    );

    return createPortal(
        <div
            className={`${s.overlay} ${isClosing ? s.overlayOut : ''}`}
            onClick={closeUnlessAdminConsumed(onClose)}
        >
            <div
                className={`${s.sheet} ${s.sheetNarrow} ${isClosing ? s.sheetOut : ''}`}
                role="dialog"
                aria-modal="true"
                aria-label={title}
                onClick={(e) => e.stopPropagation()}
            >
                <header className={s.head}>
                    <span className={`${s.headIcon} ${s.headIconAccent}`}>
                        <UserPlus size={15} strokeWidth={2.2} aria-hidden="true" />
                    </span>
                    <div className={c.headText}>
                        <h2 className={s.headTitle}>{title}</h2>
                        <span className={c.headSub}>{subtitle}</span>
                    </div>
                    <button type="button" className={s.closeBtn} onClick={onClose} aria-label={safeT('modals.code.cancelButton', 'Cancelar')}>
                        <X size={15} strokeWidth={2.2} aria-hidden="true" />
                    </button>
                </header>

                <div className={s.body}>
                    <Timeline items={timeline} />

                    {isInput ? (
                        <>
                            {/* ── Стадия 1: форма данных клиента */}
                            <section className={c.form}>
                                <div className={c.formGrid}>
                                    <div className={c.field}>
                                        <span className={c.fieldLabel}>
                                            <UserRound size={12} strokeWidth={2.1} aria-hidden="true" />
                                            {safeT('modals.code.userDetails.title', 'Cliente')}
                                        </span>
                                        <input
                                            ref={nameRef}
                                            className={c.control}
                                            value={nameValue || ''}
                                            onChange={(e) => onNameChange?.(e.target.value)}
                                            placeholder={safeT('modals.code.inputModal.namePlaceholder', 'Nombre del cliente')}
                                        />
                                    </div>
                                    <div className={c.field}>
                                        <span className={c.fieldLabel}>
                                            <Landmark size={12} strokeWidth={2.1} aria-hidden="true" />
                                            {safeT('modals.code.userDetails.bank', 'Banco')}
                                        </span>
                                        <NovaSelect
                                            value={bankValue || ''}
                                            placeholder={safeT('modals.code.inputModal.bankPlaceholder', 'Seleccione banco')}
                                            options={bankOptions}
                                            onChange={onBankChange}
                                            leadingIcon={Landmark}
                                        />
                                    </div>
                                    <div className={c.field}>
                                        <span className={c.fieldLabel}>
                                            <Tag size={12} strokeWidth={2.1} aria-hidden="true" />
                                            {safeT('modals.code.userDetails.tariff', 'Tarifa')}
                                        </span>
                                        <NovaSelect
                                            value={tariffValue || ''}
                                            placeholder={safeT('modals.code.inputModal.tariffPlaceholder', 'Seleccione tarifa')}
                                            options={tariffOptions}
                                            onChange={onTariffChange}
                                            leadingIcon={Tag}
                                        />
                                    </div>
                                    <div className={c.field}>
                                        <span className={c.fieldLabel}>
                                            <CreditCard size={12} strokeWidth={2.1} aria-hidden="true" />
                                            {accountLabel || 'CBU/CVU'}
                                        </span>
                                        <input
                                            className={`${c.control} ${c.controlMono}`}
                                            value={accountValue || ''}
                                            onChange={(e) => onAccountChange?.(e.target.value)}
                                            placeholder={safeT('modals.code.inputModal.ibanShort', 'CBU/CVU del cliente')}
                                        />
                                    </div>
                                </div>
                            </section>

                            {/* ── Брокер: имя + проверенная лицензия, справа — дефицит мест */}
                            <div className={c.brokerStrip}>
                                <span className={c.brokerGlyph}>
                                    {brokerAvatarSrc
                                        ? <img className={c.avatarImg} src={brokerAvatarSrc} alt="" />
                                        : <Briefcase size={13} strokeWidth={2.1} aria-hidden="true" />}
                                </span>
                                <div className={c.brokerId}>
                                    <span className={c.brokerName}>{brokerName || '—'}</span>
                                    <LicenseChip license={brokerLicense} safeT={safeT} />
                                </div>
                                <div className={c.brokerSpots}>
                                    <span className={c.spotsCaption}>
                                        {safeT('modals.code.brokerDetails.remainingSpots', 'Lugares restantes hoy')}
                                    </span>
                                    <SpotsMeter spots={brokerSpots} onChange={onSpotsChange} />
                                </div>
                            </div>

                            {codeSection}
                        </>
                    ) : (
                        <>
                            {/* ── Стадия 2: код — герой, данные — справка ниже */}
                            {codeSection}

                            {/* Клиент ↔ брокер: две стороны сделки в одном блоке */}
                            <section className={c.match}>
                                <div className={c.party}>
                                    <div className={c.partyHead}>
                                        <span className={c.partyAvatar}>{initialsOf(clientName)}</span>
                                        <div className={c.partyId}>
                                            <span className={c.partyRole}>{safeT('modals.code.userDetails.title', 'Cliente')}</span>
                                            <span className={c.partyName}>{clientName || '—'}</span>
                                        </div>
                                    </div>
                                    <div className={c.partyRows}>
                                        <div className={c.partyRow}>
                                            <span className={c.partyRowLabel}>
                                                <Landmark size={11} strokeWidth={2.1} aria-hidden="true" />
                                                {safeT('modals.code.userDetails.bank', 'Banco')}
                                            </span>
                                            <span className={c.partyRowValue}>
                                                {clientBank?.img && <img className={c.optIcon} src={clientBank.img} alt="" />}
                                                {clientBank?.name || '—'}
                                            </span>
                                        </div>
                                        <div className={c.partyRow}>
                                            <span className={c.partyRowLabel}>
                                                <Tag size={11} strokeWidth={2.1} aria-hidden="true" />
                                                {safeT('modals.code.userDetails.tariff', 'Tarifa')}
                                            </span>
                                            <span className={c.partyRowValue}>{clientTariffDisplay || '—'}</span>
                                        </div>
                                        <div className={c.partyRow}>
                                            <span className={c.partyRowLabel}>
                                                <CreditCard size={11} strokeWidth={2.1} aria-hidden="true" />
                                                {accountLabel || 'CBU/CVU'}
                                            </span>
                                            <span className={`${c.partyRowValue} ${c.partyRowMono}`}>{clientAccount || '—'}</span>
                                        </div>
                                    </div>
                                </div>

                                <div className={c.matchDivider} aria-hidden="true">
                                    <span className={c.matchLink}>
                                        <ArrowLeftRight size={11} strokeWidth={2.2} />
                                    </span>
                                </div>

                                <div className={c.party}>
                                    <div className={c.partyHead}>
                                        <span className={`${c.partyAvatar} ${c.partyAvatarBroker}`}>
                                            {brokerAvatarSrc
                                                ? <img className={c.avatarImg} src={brokerAvatarSrc} alt="" />
                                                : <Briefcase size={14} strokeWidth={2.1} aria-hidden="true" />}
                                        </span>
                                        <div className={c.partyId}>
                                            <span className={c.partyRole}>{safeT('modals.code.brokerDetails.title', 'Broker')}</span>
                                            <span className={c.partyName}>{brokerName || '—'}</span>
                                        </div>
                                    </div>
                                    <div className={c.partyRows}>
                                        <div className={c.partyRow}>
                                            <span className={c.partyRowLabel}>
                                                <ShieldCheck size={11} strokeWidth={2.1} aria-hidden="true" />
                                                {safeT('modals.code.brokerDetails.license', 'Licencia')}
                                            </span>
                                            <LicenseChip license={brokerLicense} safeT={safeT} />
                                        </div>
                                        <div className={c.partyRow}>
                                            <span className={c.partyRowLabel}>
                                                <Users size={11} strokeWidth={2.1} aria-hidden="true" />
                                                {safeT('modals.code.brokerDetails.remainingSpots', 'Lugares restantes hoy')}
                                            </span>
                                            <SpotsMeter spots={brokerSpots} onChange={onSpotsChange} align="start" showIcon={false} />
                                        </div>
                                    </div>
                                </div>
                            </section>
                        </>
                    )}
                </div>

                <footer className={s.foot}>
                    <div className={`${s.footActions} ${c.footActions}`}>
                        <button type="button" className={s.ghostBtn} onClick={onClose}>
                            {safeT('modals.code.cancelButton', 'Cancelar')}
                        </button>
                        <button type="button" className={s.primaryBtn} onClick={onConfirm}>
                            <Check size={15} strokeWidth={2.6} aria-hidden="true" />
                            {safeT('modals.code.submitButton', 'Confirmar')}
                        </button>
                    </div>
                </footer>
            </div>
        </div>,
        host,
    );
}
